import { Delegate } from "./Delegate";
import { UserMeta } from "./Meta";
import { Tag } from "./Tag";
import { UserPreferences } from "./UserPreferences";

export interface User {
    id: ID;
    name: string;
    email: string;
    emailCanonical: string;
    newEmail?: string;
    phone: string;
    apiKey?: string;
    roles: Array<UserRole | DelegateRole>;
    type: UserType;
    enabled: boolean;
    username: string;
    usernameCanonical: string;
    delegates: Delegate[];
    lastLogin: any;
    preferences: UserPreferences;
    meta?: UserMeta;
    tags?: Tag[];
    locale?: string;
    xolaTermsOfService?: { version: string };
    isMerged?: boolean;
    mergedInto?: { id: ID };
    mergedAt?: string;
    restoresSurvivor?: RestoresSurvivor;
}

export interface RestoredValue {
    old: string | null;
    new: string | null;
}

export interface RestoresSurvivor {
    name?: RestoredValue;
    email?: RestoredValue;
    phone?: RestoredValue;
}

export enum UserType {
    SELLER = 1,
    TRAVELER = 2,
    USER = 3,
}

export enum UserRole {
    SELLER = "ROLE_SELLER",
    ADMIN = "ROLE_ADMIN",
    SUPER_ADMIN = "ROLE_SUPER_ADMIN",
    TRAVELER = "ROLE_TRAVELER",
    API_KEY = "ROLE_API_KEY",
}

export enum DelegateRole {
    GUIDE = "ROLE_GUIDE",
    CURATOR = "ROLE_CURATOR",
    GUIDE_MANAGER = "ROLE_GUIDE_MANAGER",
    WARE_MANAGER = "ROLE_WARE_MANAGER",
    EQUIPMENT_MANAGER = "ROLE_EQUIPMENT_MANAGER",
    SELLER_ADMIN = "ROLE_SELLER_ADMIN",
    RESERVATION = "ROLE_RESERVATION",
    RESERVATION_LITE = "ROLE_RESERVATION_LITE",
    MARKETER = "ROLE_MARKETER",
    WEBSITE_MANAGER = "ROLE_WEBSITE_MANAGER",
    ACCOUNTANT = "ROLE_ACCOUNTANT",
    AFFILIATE = "ROLE_AFFILIATE",
    PLUGIN = "ROLE_PLUGIN_USER",
    BOOKING_ENGINE = "ROLE_BOOKING_ENGINE",
    XOLA_PARTNER = "ROLE_XOLA_PARTNER",
}

export enum UserRolesCopy {
    ROLE_SELLER_ADMIN = "Administrator",
    ROLE_CURATOR = "Curator",
    ROLE_EQUIPMENT_MANAGER = "Equipment Manager",
    ROLE_RESERVATION = "Reservationist",
    ROLE_RESERVATION_LITE = "Reservationist Lite",
    ROLE_GUIDE_MANAGER = "Guide Manager",
    ROLE_WARE_MANAGER = "Merchandise Manager",
    ROLE_ACCOUNTANT = "Accountant",
    ROLE_GUIDE = "Guide",
    ROLE_MARKETER = "Marketer",
    ROLE_WEBSITE_MANAGER = "Website Manager",
    ROLE_AFFILIATE = "Agent",
    ROLE_PLUGIN_USER = "Plugin User",
    ROLE_BOOKING_ENGINE = "Booking Engine User",
    ROLE_TRAVELER = "Customer",
    ROLE_XOLA_PARTNER = "Xola Partner",
    ROLE_SUPER_ADMIN = "Super Admin",
    ROLE_ADMIN = "Admin",
    ROLE_SELLER = "Seller",
}

export const DelegateRoleMapping = [
    {
        name: UserRolesCopy.ROLE_SELLER_ADMIN,
        id: DelegateRole.SELLER_ADMIN,
        text: "has complete access to this account",
    },
    {
        name: UserRolesCopy.ROLE_CURATOR,
        id: DelegateRole.CURATOR,
        text: "can create/edit products",
    },
    {
        name: UserRolesCopy.ROLE_EQUIPMENT_MANAGER,
        id: DelegateRole.EQUIPMENT_MANAGER,
        text: "can assign equipment to events",
    },
    {
        name: UserRolesCopy.ROLE_RESERVATION,
        id: DelegateRole.RESERVATION,
        text: "can create/edit purchases and view the dashboard, rosters and customers",
    },
    {
        name: UserRolesCopy.ROLE_RESERVATION_LITE,
        id: DelegateRole.RESERVATION_LITE,
        text: "can create and modify purchases with no refund privileges",
    },
    {
        name: UserRolesCopy.ROLE_GUIDE_MANAGER,
        id: DelegateRole.GUIDE_MANAGER,
        text: "can assign guides to events",
    },
    {
        name: UserRolesCopy.ROLE_WARE_MANAGER,
        id: DelegateRole.WARE_MANAGER,
        text: "can create/edit merchandise products and manage inventory",
    },
    {
        name: UserRolesCopy.ROLE_ACCOUNTANT,
        id: DelegateRole.ACCOUNTANT,
        text: "can view all purchases and financial data",
    },
    {
        name: UserRolesCopy.ROLE_GUIDE,
        id: DelegateRole.GUIDE,
        text: "can be assigned to events by Guide Managers",
    },
    {
        name: UserRolesCopy.ROLE_MARKETER,
        id: DelegateRole.MARKETER,
        text: "can access the Marketing tab, create & manage coupon campaigns and access button code",
    },
    {
        name: UserRolesCopy.ROLE_WEBSITE_MANAGER,
        id: DelegateRole.WEBSITE_MANAGER,
        text: "can access the XSite tab and manage website content and settings",
    },
];
